// Vendor Imports
import classNames from 'classnames';
import React from 'react';

// Project Imports
import DebouncedSlider from '../../shared/DebouncedSlider';
import { I18n } from 'common/i18n';
import {
  MEASURE_AXIS_PRECISION_AUTOMATIC,
  MEASURE_AXIS_PRECISION_CUSTOM,
} from 'common/authoring_workflow/constants';

const scope = 'shared.visualizations.panes.axis.fields.precision';

const renderRadioButton = ({
  checked,
  className,
  disabled,
  id,
  label,
  onChange,
  value,
}: {
  checked: boolean;
  className: string;
  disabled?: boolean;
  id: string;
  label: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value: string;
}) => {
  const radioButtonContainerClassName = `${className} ${id}-selector`;

  return (
    <div className={radioButtonContainerClassName}>
      <input
        checked={checked}
        disabled={disabled}
        id={id}
        onChange={onChange}
        name={className}
        type="radio"
        value={value}
      />
      <label htmlFor={id}>
        <span className="fake-radiobutton" />
        <div className="translation-within-label">{label}</div>
      </label>
    </div>
  );
};

export const AxisPrecisionSelector = ({
  id = 'axis-precision',
  className = 'axis-precision-container',
  disabled,
  onRadioButtonChange,
  onSliderChange,
  precision,
  customPrecisionDisabled,
}: {
  id?: string;
  className?: string;
  disabled?: boolean;
  onRadioButtonChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSliderChange?: (value: number) => void;
  precision?: number;
  customPrecisionDisabled?: boolean;
}) => {
  const axisPrecisionClassName = classNames('authoring-field radiobutton', {
    disabled,
  });

  const automaticRadioButton = renderRadioButton({
    checked: !Number.isFinite(precision),
    className,
    disabled,
    id: `${id}-automatic`,
    label: I18n.t('automatic', { scope }),
    onChange: onRadioButtonChange!,
    value: MEASURE_AXIS_PRECISION_AUTOMATIC,
  });

  const customRadioButton = renderRadioButton({
    checked: Number.isFinite(precision),
    className: classNames(className, { disabled: disabled || customPrecisionDisabled }),
    disabled: disabled || customPrecisionDisabled,
    id: `${id}-custom`,
    label: I18n.t('custom', { scope }),
    onChange: onRadioButtonChange!,
    value: MEASURE_AXIS_PRECISION_CUSTOM,
  });

  return (
    <div className={axisPrecisionClassName}>
      {automaticRadioButton}
      {customRadioButton}
      <div className="measure-axis-precision-slider-container">
        <DebouncedSlider
          disabled={disabled || customPrecisionDisabled}
          onChange={onSliderChange!}
          rangeMax={4}
          rangeMin={0}
          step={1}
          value={precision || 0}
        />
      </div>
    </div>
  );
};
